import type { ReactNode } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import AccessDenied from '../pages/AccessDenied';
import { usePermissions } from '../components/ccms-components/auth/hooks/usePermissions';
import MemberDirectoryPage from '../pages/admin/Members/index';
import MemberDetailPage from '../pages/admin/Members/MemberDetail';
import MemberCustomFieldsPage from '../pages/admin/Members/CustomFields';

/**
 * Admin member directory + profiles: /admin/members and /admin/members/:memberId, plus the member fields
 * (custom field definitions) at /admin/members/custom-fields.
 *
 * Mounted in App.tsx OUTSIDE the Admin-only /admin/* gate because the backend lets the Admin role
 * OR the Member.Read right read members, and ProtectedRoute ANDs roles with permissions.
 */
function MembersGate({ children }: { children: ReactNode }) {
  const { hasRole, hasPermission } = usePermissions();
  if (!hasRole('Admin') && !hasPermission('Member.Read')) return <AccessDenied />;
  return <>{children}</>;
}

/** Member fields (custom field definitions): the Admin role OR the Member.CustomFields.Manage right (identity 011). */
function MemberFieldsGate({ children }: { children: ReactNode }) {
  const { hasRole, hasPermission } = usePermissions();
  if (!hasRole('Admin') && !hasPermission('Member.CustomFields.Manage')) return <AccessDenied />;
  return <>{children}</>;
}

export default function AdminMembersRoutes() {
  return (
    <Routes>
      <Route index element={<MainLayout><MembersGate><MemberDirectoryPage /></MembersGate></MainLayout>} />
      <Route path="custom-fields" element={<MainLayout><MemberFieldsGate><MemberCustomFieldsPage /></MemberFieldsGate></MainLayout>} />
      <Route path=":memberId" element={<MainLayout><MembersGate><MemberDetailPage /></MembersGate></MainLayout>} />
      <Route path="*" element={<Navigate to="/admin/members" replace />} />
    </Routes>
  );
}
